/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getFiles = (feature: BaseFeature): string[] => {
    return [
        'settings.xtxt',
        'libraries/settings/settings.tbh',
        'libraries/settings/settings.tbs',
    ];
};

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    let blocks: Array<CodeBlock> = [];
    const settings = feature.getSettingValue('SETTINGS');
    if (!feature.getEnabled()) {
        return blocks;
    }

    const { project } = feature;
    const LED_PAT = project.getSettingValue('general', 'LEDPAT') === '1';
    let RAM_ARRAY_SIZE = 0;
    const STG_TIMESTAMP = feature.getSettingValue('STG_TIMESTAMP');

    if (settings.length > 0) {
        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    'Setting Library Initialization
    '================================================================
    dim stg_init_code as en_stg_status_codes
    dim stg_name as string(STG_MAX_SETTING_NAME_LEN)
    if stg_start()<>EN_STG_STATUS_OK then 
        ${LED_PAT ? 'pat.play("R-R-~",PL_PAT_CANINT)' : ''}
        sys.debugprint("stg_start failed")
        sys.halt
    end if
    stg_init_code=stg_check_all(stg_name)
    select case stg_init_code
    case EN_STG_STATUS_OK:
    '--- all good ---
    case EN_STG_STATUS_INVALID, EN_STG_STATUS_FAILURE:
        sys.debugprint("Settings initialization failed, restoring default values")
        if stg_restore_multiple(EN_STG_INIT_MODE_NORMAL)<>EN_STG_STATUS_OK then 
            ${LED_PAT ? 'pat.play("R-R-~",PL_PAT_CANINT)' : ''}
            sys.debugprint("stg_restore_multiple failed")
            sys.halt
        end if
    case else:
        'some other trouble
        ${LED_PAT ? 'pat.play("R-R-~",PL_PAT_CANINT)' : ''}
        sys.debugprint("stg_check_all failed")
        sys.halt
    end select
    '-----------------------------------------------------------------`,
        });
    }


    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_stg_error',
        template: `
sub callback_stg_error(byref stg_name_or_num as string,index as byte,status as en_stg_status_codes)
    '****callback_stg_error****'
end sub
        `,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_stg_pre_get',
        template: `
sub callback_stg_pre_get(byref stg_name_or_num as string,index as byte,byref stg_value as string)
    '****callback_stg_pre_get****'
end sub
        `,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_stg_post_set',
        template: `
sub callback_stg_post_set(byref stg_name_or_num as string, index as byte,byref stg_value as string)
    '****callback_stg_post_set****'
end sub
        `,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_stg_vm_read',
        template: `
function callback_stg_vm_read(address as word) as byte
    '****callback_stg_vm_read****'
end function
        `,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_stg_vm_write',
        template: `
sub callback_stg_vm_write(data_to_write as byte, address as word)
    '****callback_stg_vm_write****'
end sub`,
    });


    let maxNameLength = 0;
    let maxValueLength = 0;
    const totalBytes = 0;
    for (let i = 0; i < settings.length; i += 1) {
        const setting = settings[i];
        const settingBlocks = setting.getCodeBlocks();
        blocks = blocks.concat(settingBlocks);
        if (setting.getSettingValue('SETTINGID').length > maxNameLength) {
            maxNameLength = setting.getSettingValue('SETTINGID').length;
        }
        const data_type = setting.getSettingValue('DATATYPE');

        let required_str_len = 0;
        let byte_size = 0;
        switch (data_type) {
            case 'B':
                byte_size = 1;
                required_str_len = 3;
                break;
            case 'W':
                byte_size = 2;
                required_str_len = 5;
                break;
            case 'F':
                byte_size = 4;
                required_str_len = 4;
                break;
            case 'S':
                required_str_len = Number(setting.getSettingValue('MAX'));
                if (required_str_len === 0) {
                    required_str_len = Number(setting.getDefault('MAX'));
                }
                byte_size = required_str_len + 1;
                break;
            case 'D':
                required_str_len = Number(setting.getSettingValue('MAX'));
                if (required_str_len === 0) {
                    required_str_len = Number(setting.getDefault('MAX'));
                }
                byte_size = required_str_len + 1;
                required_str_len *= 4;
                if (required_str_len > 0) required_str_len -= 1;
                break;
            case 'T': // datetime timestamp
            case 'M': // Time
                byte_size = 4;
                required_str_len = 10;
                break;
            default:

                break;
        }
        if (maxValueLength < required_str_len) {
            maxValueLength = required_str_len;
        }
        byte_size = byte_size * Number(1) + 1;
        if (STG_TIMESTAMP === '1') {
            byte_size += 7;
        }
        if (setting.getSettingValue('STORAGE') === 'R') {
            RAM_ARRAY_SIZE += byte_size;
        }
    }

    let settingsFile = '';
    settingsFile += `
#define STG_DESCRIPTOR_FILE "settings.xtxt"
#define STG_DEBUG_PRINT ${feature.getSettingValue('STG_DEBUG_PRINT')}`;

    if (STG_TIMESTAMP === '1') {
        settingsFile += `
#define STG_TIMESTAMP 1`;
    }
    if (feature.getSettingValue('REDUNDANCY') !== '0') {
        settingsFile += `
#define STG_REDUNDANCY ${feature.getSettingValue('REDUNDANCY')}`;
    }

    settingsFile += `
#define STG_MAX_NUM_SETTINGS ${settings.length}
#define STG_RAM_ARRAY_SIZE ${RAM_ARRAY_SIZE}
#define STG_MAX_SETTING_NAME_LEN ${maxNameLength}
#define STG_MAX_SETTING_VALUE_LEN ${maxValueLength}`;


    blocks.push({
        location: 'SETTINGS_XTXT',
        blockType: BlockType.CODE,
        template: settingsFile,
    });

    return blocks;
};
